import { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import ConfirmationModal from '../../../shared/components/modals/ConfirmationModal';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import { EmployeeIDBadge } from '../../../shared/components/common/Badges';
import { toBoolean } from '../../../utils/booleanUtils';
import { formatPhoneNumber, removeCountryCode, validateIndianPhoneNumber } from '../../../utils/phoneFormatter';
import './UserFormModal.css';

function UserFormModal({ user, locations, departments, onSubmit, onCancel, usernameError, modalError, selectedLocations, setSelectedLocations, selectedDepartments, setSelectedDepartments, currentUser }) {
  const modalContentRef = useRef(null);
  const errorMessageRef = useRef(null);
  const [employeeIdMode, setEmployeeIdMode] = useState('manual'); // 'auto' or 'manual'
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    contact_number: '',
    employee_id: '',
    role: 'central_help_desk',
    is_active: false,
  });
  const [statusConfirmation, setStatusConfirmation] = useState({ isOpen: false, newStatus: null });
  const [isConfirmingStatus, setIsConfirmingStatus] = useState(false);
  const [emailHelpOpen, setEmailHelpOpen] = useState(false);
  const [departmentSearch, setDepartmentSearch] = useState('');

  // Fetch EMPLOYEE_ID_MODE from environment
  useEffect(() => {
    const mode = process.env.REACT_APP_EMPLOYEE_ID_MODE || 'manual';
    setEmployeeIdMode(mode.toLowerCase());
  }, []);

  // Auto-scroll to error message when it appears
  useEffect(() => {
    if (modalError && errorMessageRef.current && modalContentRef.current) {
      // Scroll the modal content to top
      modalContentRef.current.scrollTop = 0;
      // Focus on the error message for accessibility
      errorMessageRef.current.focus();
    }
  }, [modalError]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const availableDepartments = useMemo(() => {
    if (!departments) return [];
    if (['coordinator', 'manager'].includes(currentUser?.role)) {
      const allowedIds = new Set(
        (currentUser?.department_ids || currentUser?.departments?.map((d) => d.id) || []).map(String)
      );
      if (allowedIds.size > 0) {
        return departments.filter((dept) => allowedIds.has(String(dept.id)));
      }
    }
    return departments;
  }, [departments, currentUser]);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email,
        full_name: user.full_name,
        password: '',
        contact_number: user.contact_number ? removeCountryCode(user.contact_number) : '',
        employee_id: user.employee_id || '',
        role: user.role,
        is_active: toBoolean(user.is_active)
      });
    } else {
      // Auto-select location if only one is available and creating new user
      if (locations.length === 1 && selectedLocations.length === 0) {
        setSelectedLocations([locations[0].id]);
      }
      if (availableDepartments.length === 1 && selectedDepartments.length === 0) {
        setSelectedDepartments([availableDepartments[0].id]);
      }
      // Phase 1: New users start INACTIVE (is_active = false) until email verification
      setFormData(prev => ({
        ...prev,
        is_active: false
      }));
    }
  }, [user, locations, selectedLocations.length, selectedDepartments.length, currentUser, availableDepartments]);

  useEffect(() => {
    setEmailHelpOpen(false);
    setDepartmentSearch('');
  }, [user?.id]);

  const filteredDepartments = useMemo(() => {
    const query = departmentSearch.trim().toLowerCase();
    if (!query) return availableDepartments;
    return availableDepartments.filter((dept) =>
      String(dept.name || '').toLowerCase().includes(query)
    );
  }, [availableDepartments, departmentSearch]);

  // Check if selected role is location-scoped
  const isLocationScopedRole = ['central_help_desk', 'admin', 'manager'].includes(formData.role);
  const isDepartmentScopedRole = ['technician', 'custodian', 'coordinator'].includes(formData.role);
  const isDepartmentSelectionLocked = !!(user && (currentUser?.role === 'coordinator' || currentUser?.role === 'manager'));

  // Same boolean rules as UsersPage / API (0/1, "true"/"false", native boolean)
  const emailVerifiedBool = user ? toBoolean(user.email_verified) : false;
  // Inactive users: banner + status UX (verification vs deactivated)
  const isInactiveUser = !!(user && !toBoolean(user.is_active));
  // Read-only form only for verified-but-deactivated accounts — not for pending verification
  // (fixes: typo after email change / revoked link → admin can edit and save, then resend verification)
  const isDeactivateReadOnly = !!(user && !toBoolean(user.is_active) && emailVerifiedBool);

  // Pending verification workflow:
  // If a non-expired verification link exists, lock edits to avoid mismatch with the issued link.
  // Admin can revoke the link to unlock edits (fix typos), then send a new verification email.
  const hasActivePendingLink = !!(user?.pending_link && !user?.pending_link?.is_expired);
  const isPendingVerification = !!(user && !toBoolean(user.is_active) && !emailVerifiedBool);
  const isVerificationLinkLock = !!(isPendingVerification && hasActivePendingLink);
  const isFormReadOnly = !!(isDeactivateReadOnly || isVerificationLinkLock);

  const emailChangedFromSaved =
    user && String(formData.email || '').trim() !== String(user.email || '').trim();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // For contact_number, keep only digits and limit to 10
    // Also prevent starting with 0 (invalid for Indian phone numbers)
    let finalValue = value;
    if (name === 'contact_number') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      // Prevent number starting with 0 - if user tries to enter 0, don't add it
      if (digits.length === 1 && digits === '0') {
        finalValue = '';
      } else if (digits.length > 1 && digits.startsWith('0')) {
        // If user pastes a number starting with 0, reject the entire input
        finalValue = '';
      } else {
        finalValue = digits;
      }
    }

    // Handle status toggle with confirmation when editing existing user
    if (name === 'is_active' && user) {
      const newStatus = !formData.is_active;
      setStatusConfirmation({
        isOpen: true,
        newStatus: newStatus
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : finalValue
    }));

    // Clear departments when switching to location-scoped role
    if (name === 'role' && ['central_help_desk', 'admin', 'manager'].includes(value)) {
      setSelectedDepartments([]);
    }
  };

  const handleStatusConfirm = () => {
    setIsConfirmingStatus(true);

    // Phase 2: Check if trying to activate a deactivated user
    // Users cannot be activated via the edit form - they must use the reactivation link
    if (statusConfirmation.newStatus === true && user && !toBoolean(user.is_active)) {
      // Cancel confirmation and let parent component handle the error
      setStatusConfirmation({ isOpen: false, newStatus: null });
      setIsConfirmingStatus(false);
      // Parent component will show error via toast
      return;
    }

    setFormData(prev => ({
      ...prev,
      is_active: statusConfirmation.newStatus
    }));
    setStatusConfirmation({ isOpen: false, newStatus: null });
    setIsConfirmingStatus(false);
  };

  const handleStatusCancel = () => {
    setStatusConfirmation({ isOpen: false, newStatus: null });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate contact_number if provided
    if (formData.contact_number) {
      const validation = validateIndianPhoneNumber(formData.contact_number);
      if (!validation.isValid) {
        // Prevention: This shouldn't happen due to handleChange logic, but as a safety measure
        return;
      }
    }

    // Validate employee_id in MANUAL mode (required field)
    if (employeeIdMode === 'manual' && !user && !formData.employee_id?.trim()) {
      // Employee ID is required in MANUAL mode for new users
      // Don't submit - let the validation display message guide user
      return;
    }

    // Phase 1: Remove password field from submission (system-generated via email verification)
    // Create a copy of formData without password
    const { password, ...formDataWithoutPassword } = formData;

    // In AUTO mode, don't include employee_id in submission (backend will auto-generate)
    // In MANUAL mode, include employee_id in submission (user provides it)
    if (employeeIdMode === 'auto') {
      const { employee_id, ...finalData } = formDataWithoutPassword;
      onSubmit(finalData);
    } else {
      onSubmit(formDataWithoutPassword);
    }
  };

  return ReactDOM.createPortal(
    <div className="user-form-modal-overlay">
      <div className="user-form-modal">
        <div className="user-form-modal-header">
          <div className="user-form-modal-header-text">
            <h2>{user ? 'Edit User' : 'Create New User'}</h2>
            {user && <EmployeeIDBadge employeeId={user.employee_id} size="medium" />}
          </div>
          <ModalCloseButton onClick={onCancel} title="Close" />
        </div>

        <form onSubmit={handleSubmit} className="user-form-modal-form">
          <div className="user-form-modal-body" ref={modalContentRef}>
          {isInactiveUser && (
            <div className={`user-form-banner ${emailVerifiedBool ? 'user-form-banner--locked' : 'user-form-banner--pending'}`}>
              <span className="user-form-banner-icon" aria-hidden="true">{emailVerifiedBool ? '🔒' : '📧'}</span>
              <div>
                <p className="user-form-banner-title">
                  {emailVerifiedBool ? 'Account Locked (Deactivated)' : 'Account Pending Verification'}
                </p>
                <p>
                  {emailVerifiedBool ? (
                    user?.pending_link ? (
                      <>
                        A reactivation link was sent on <strong>{new Date(user.pending_link.created_at).toLocaleDateString()}</strong> at <strong>{new Date(user.pending_link.created_at).toLocaleTimeString()}</strong>. The user can click the link in their email to reactivate this account. The link expires on <strong>{new Date(user.pending_link.expires_at).toLocaleDateString()}</strong> at <strong>{new Date(user.pending_link.expires_at).toLocaleTimeString()}</strong>.
                      </>
                    ) : (
                      <>
                        This account is read-only until reactivated. Use &quot;Send Reactivation Link&quot; in the user list to unlock this account.
                      </>
                    )
                  ) : (
                    user?.pending_link ? (
                      <>
                        A verification email was sent on <strong>{new Date(user.pending_link.created_at).toLocaleDateString()}</strong> at <strong>{new Date(user.pending_link.created_at).toLocaleTimeString()}</strong>. The user must click the link in their email to verify and activate this account. The link expires on <strong>{new Date(user.pending_link.expires_at).toLocaleDateString()}</strong> at <strong>{new Date(user.pending_link.expires_at).toLocaleTimeString()}</strong>.
                        {hasActivePendingLink && (
                          <span style={{ display: 'block', marginTop: '0.65rem' }}>
                            <strong>Need to fix a typo?</strong> This record cannot be edited while a verification link is active. In the user list, open the actions menu (⋮) and choose <strong>Revoke Pending Link</strong>, then edit and save. If you <strong>change the email</strong> and save, a new verification email is sent automatically. If you <strong>do not</strong> change the email, save your other edits, then choose <strong>Send Verification Email</strong> or <strong>Resend Verification Email</strong> from the actions menu (⋮) to issue a new link.
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        No active verification link—you can edit this user&apos;s details below. Saving with an <strong>updated email</strong> sends a new verification email automatically. If the email is <strong>unchanged</strong>, save any other edits, then use <strong>Send Verification Email</strong> or <strong>Resend Verification Email</strong> from the actions menu (⋮) to deliver a link.
                      </>
                    )
                  )}
                </p>
              </div>
            </div>
          )}

          {modalError && (
            <div
              ref={errorMessageRef}
              tabIndex="-1"
              className="user-form-banner user-form-banner--error"
            >
              <span className="user-form-banner-icon" aria-hidden="true">⚠️</span>
              <div>
                <p className="user-form-banner-title">{modalError}</p>
              </div>
            </div>
          )}

          {emailChangedFromSaved && (
            <div className="user-form-banner user-form-banner--warning">
              <div>
                <strong>Email change:</strong> saving will require the user to verify the new address. The account will be
                set to <strong>pending verification</strong> until they complete the link in the verification email. If the
                old email was already verified, a security notice is sent there. Updated username (if any) is included in
                the <strong>confirmation email after</strong> successful verification—not in the initial verification email.
              </div>
            </div>
          )}

          <div className="user-form-section-title">Personal Information</div>

          {/* MANUAL MODE: Row 1: Employee ID & Full Name */}
          {employeeIdMode === 'manual' && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="employee_id">
                  Employee ID {!user && '*'}
                </label>
                <input
                  type="text"
                  id="employee_id"
                  name="employee_id"
                  value={formData.employee_id}
                  onChange={handleChange}
                  required={!user}
                  disabled={!!user || isFormReadOnly}
                  placeholder="e.g., EMP12345, ACME-E001"
                  maxLength="50"
                />
                {user && <small className="user-form-hint">Cannot be changed</small>}
                {!user && (
                  <small className="user-form-hint">
                    Provide the employee ID from your HRIS or external system
                  </small>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="full_name">Full Name *</label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  disabled={isFormReadOnly}
                  placeholder="John Doe"
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* AUTO MODE: Row 1: Full Name & Email */}
          {employeeIdMode === 'auto' && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="full_name">Full Name *</label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  disabled={isFormReadOnly}
                  placeholder="John Doe"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label className="user-form-label-with-info" htmlFor="email">
                  <span>Email Address *</span>
                  {user && (
                    <button
                      type="button"
                      className="user-form-info-btn"
                      onClick={() => setEmailHelpOpen((o) => !o)}
                      aria-expanded={emailHelpOpen}
                      aria-controls="email-change-help"
                      title={emailHelpOpen ? 'Hide email change help' : 'Email change workflow'}
                    >
                      <span className="user-form-info-icon" aria-hidden="true">ℹ️</span>
                      <span className="sr-only">Email change workflow help</span>
                    </button>
                  )}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isFormReadOnly}
                  placeholder="john.smith@dhi.com"
                />
                {user && emailHelpOpen && (
                  <div id="email-change-help" className="user-form-help-panel" role="note">
                    Changing the email moves this account to <strong>inactive</strong> until the new address is verified.
                    We send a verification link to the new inbox; if the old email was already verified, a short security
                    notice may go there too. The user can sign in again only after they complete verification.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MANUAL MODE: Row 2: Email & Contact Number */}
          {employeeIdMode === 'manual' && (
            <div className="form-row">
              <div className="form-group">
                <label className="user-form-label-with-info" htmlFor="email">
                  <span>Email Address *</span>
                  {user && (
                    <button
                      type="button"
                      className="user-form-info-btn"
                      onClick={() => setEmailHelpOpen((o) => !o)}
                      aria-expanded={emailHelpOpen}
                      aria-controls="email-change-help"
                      title={emailHelpOpen ? 'Hide email change help' : 'Email change workflow'}
                    >
                      <span className="user-form-info-icon" aria-hidden="true">ℹ️</span>
                      <span className="sr-only">Email change workflow help</span>
                    </button>
                  )}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isFormReadOnly}
                  placeholder="john.smith@dhi.com"
                />
                {user && emailHelpOpen && (
                  <div id="email-change-help" className="user-form-help-panel" role="note">
                    Changing the email moves this account to <strong>inactive</strong> until the new address is verified.
                    We send a verification link to the new inbox; if the old email was already verified, a short security
                    notice may go there too. The user can sign in again only after they complete verification.
                  </div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="contact_number">Contact Number *</label>
                <input
                  type="tel"
                  id="contact_number"
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleChange}
                  required
                  maxLength="10"
                  placeholder="e.g., 9876543210"
                  disabled={isFormReadOnly}
                />
                {(() => {
                  const digits = formData.contact_number ? formData.contact_number.replace(/\D/g, '') : '';
                  const validation = formData.contact_number ? validateIndianPhoneNumber(formData.contact_number) : { isValid: true, error: null };
                  const statusColor = formData.contact_number && !validation.isValid ? '#d32f2f' : digits.length === 10 ? '#27ae60' : '#f59e0b';
                  const statusIcon = !formData.contact_number ? '-' : validation.isValid ? '✓' : '✗';
                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <small className="user-form-hint">For SMS/WhatsApp notifications</small>
                        <small style={{ color: statusColor, fontWeight: '600', fontSize: '0.85rem' }}>
                          {statusIcon} {formData.contact_number ? `${digits.length}/10` : '-'}
                        </small>
                      </div>
                      {formData.contact_number && !validation.isValid && (
                        <small className="error" style={{display: 'block', marginTop: '0.25rem'}}>{validation.error}</small>
                      )}
                      {user && formData.contact_number && validation.isValid && (
                        <small style={{ color: '#666', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>
                          {formatPhoneNumber(formData.contact_number)}
                        </small>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* AUTO MODE: Row 2: Contact Number & Empty */}
          {employeeIdMode === 'auto' && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contact_number">Contact Number *</label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'stretch' }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <input
                      type="tel"
                      id="contact_number"
                      name="contact_number"
                      value={formData.contact_number}
                      onChange={handleChange}
                      required
                      maxLength="10"
                      placeholder="e.g., 9876543210"
                      disabled={isFormReadOnly}
                    />
                    <small className="user-form-hint">For SMS/WhatsApp notifications</small>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', justifyContent: 'flex-start', minWidth: '180px' }}>
                    {(() => {
                      const digits = formData.contact_number ? formData.contact_number.replace(/\D/g, '') : '';
                      const validation = formData.contact_number ? validateIndianPhoneNumber(formData.contact_number) : { isValid: true, error: null };
                      const statusColor = formData.contact_number && !validation.isValid ? '#d32f2f' : digits.length === 10 ? '#27ae60' : '#f59e0b';
                      const statusText = formData.contact_number ? `${digits.length}/10` : '-';
                      const statusIcon = !formData.contact_number ? '-' : validation.isValid ? '✓' : '✗';
                      return (
                        <>
                          <small style={{ color: statusColor, fontWeight: '600', fontSize: '0.85rem', textAlign: 'center', lineHeight: '1.4', marginTop: '0.5rem' }}>
                            {statusIcon} {statusText}
                          </small>
                          {formData.contact_number && !validation.isValid && (
                            <small style={{ color: '#d32f2f', fontSize: '0.75rem', textAlign: 'center', lineHeight: '1.3', fontWeight: '500', whiteSpace: 'normal' }}>
                              {validation.error}
                            </small>
                          )}
                          {user && formData.contact_number && validation.isValid && (
                            <small style={{ color: '#666', fontSize: '0.8rem', whiteSpace: 'nowrap', lineHeight: '1.2', textAlign: 'center' }}>
                              {formatPhoneNumber(formData.contact_number)}
                            </small>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <div className="form-group"></div>
            </div>
          )}

          {employeeIdMode === 'auto' && !user && (
            <div className="user-form-notice">
              Employee ID will be automatically generated during account creation
            </div>
          )}

          <div className="user-form-section-title">Account Credentials</div>
          
          {/* Row 3: Username & Password */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="username">Username *</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                disabled={isFormReadOnly}
                placeholder="john.doe"
                className={usernameError ? 'input-error' : ''}
              />
              {usernameError && <small className="error">{usernameError}</small>}
            </div>
            <div className="form-group">
              <label htmlFor="password">
                Password {user ? '(system-generated)' : '(system-generated)'}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                disabled
                className="user-form-password-input"
                placeholder={user ? "Temporary password sent after email verification" : "Temporary password sent after email verification"}
              />
              <small className="user-form-hint">
                {user
                  ? 'Password is managed by the system via email verification'
                  : 'A temporary password will be generated and sent via email after the user verifies their email address'
                }
              </small>
            </div>
          </div>

          <div className="user-form-section-title">Role & Assignment</div>

          <div className="form-row form-row--full">
            <div className="form-group">
              <label htmlFor="role">Role *</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                disabled={isFormReadOnly}
              >
                <option value="central_help_desk">Central Help Desk</option>
                {/* Only superadmin can create admin users */}
                {currentUser?.role === 'superadmin' && (
                  <option value="admin">Admin</option>
                )}
                <option value="manager">Manager</option>
                <option value="technician">Technician</option>
                <option value="coordinator">Coordinator</option>
                <option value="custodian">Custodian</option>
              </select>
              {isLocationScopedRole && (
                <small className="user-form-hint">
                  This role is location-scoped — department assignment is not applicable
                </small>
              )}
            </div>
          </div>

          {isDepartmentScopedRole && (
            <div className="user-form-assign">
              <label className="section-label" htmlFor="user-dept-search">
                Assign Department(s) *
              </label>
              {selectedDepartments.length === 0 && (
                <small className="error">At least one department is required</small>
              )}
              {isDepartmentSelectionLocked && (
                <small className="user-form-hint">
                  {user ? 'Department assignments cannot be changed' : 'Limited to your assigned departments'}
                </small>
              )}
              <div className={`user-form-picker${isFormReadOnly || isDepartmentSelectionLocked ? ' is-locked' : ''}`}>
                <input
                  id="user-dept-search"
                  type="search"
                  className="user-form-picker-search"
                  placeholder="Search departments…"
                  value={departmentSearch}
                  onChange={(e) => setDepartmentSearch(e.target.value)}
                  disabled={isFormReadOnly || isDepartmentSelectionLocked}
                />
                <div className="user-form-checklist" role="group" aria-label="Departments">
                  {filteredDepartments.length === 0 && (
                    <div className="user-form-checklist-empty">No departments match</div>
                  )}
                  {filteredDepartments.map((dept) => {
                    const checked = selectedDepartments.includes(dept.id);
                    return (
                      <label key={dept.id} className={`user-form-checklist-item${checked ? ' is-checked' : ''}`}>
                        <input
                          type="checkbox"
                          name="department"
                          value={dept.id}
                          checked={checked}
                          onChange={(e) => {
                            const deptId = parseInt(e.target.value, 10);
                            if (e.target.checked) {
                              setSelectedDepartments((prev) => [...prev, deptId]);
                            } else {
                              setSelectedDepartments((prev) => prev.filter((id) => id !== deptId));
                            }
                          }}
                          disabled={isFormReadOnly || isDepartmentSelectionLocked}
                        />
                        <span className="user-form-checklist-label">
                          {dept.icon ? <span className="user-form-checklist-icon" aria-hidden="true">{dept.icon}</span> : null}
                          {dept.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p className="user-form-picker-meta">
                  {selectedDepartments.length} selected
                  {!isLocationScopedRole ? ' · users can belong to one or more departments' : ''}
                </p>
              </div>
            </div>
          )}

          {/* Locations Section */}
          {formData.role !== 'superadmin' && (
            <div className="locations-section">
              <label className="section-label">Assign Location *</label>
              {selectedLocations.length === 0 && formData.role !== 'superadmin' && (
                <small className="error" style={{ display: 'block' }}>Location assignment is required</small>
              )}
              <div className="locations-grid">
                {locations.map((location) => (
                  <label key={location.id} className="location-item" style={{ opacity: isFormReadOnly ? 0.6 : 1, pointerEvents: isFormReadOnly ? 'none' : 'auto', cursor: isFormReadOnly ? 'not-allowed' : 'pointer' }}>
                    <input
                      type="radio"
                      name="location"
                      value={location.id}
                      checked={selectedLocations.includes(location.id)}
                      onChange={(e) => {
                        setSelectedLocations([parseInt(e.target.value)]);
                      }}
                      disabled={isFormReadOnly}
                    />
                    <span className="assign-location-name">{location.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Status — after assignment; account lifecycle, not org mapping */}
          {(() => {
            const isStatusToggleDisabled = !user || (user && !toBoolean(user.is_active));
            let statusHint = 'New accounts automatically activate when the user verifies their email';
            let statusHintTone = '';
            if (user && toBoolean(user.is_active)) {
              statusHint = 'Deactivate to restrict user access. They can reactivate via email link.';
            } else if (user && !toBoolean(user.is_active)) {
              statusHintTone = emailVerifiedBool ? ' user-form-status-toggle-hint--warn' : ' user-form-status-toggle-hint--info';
              if (emailVerifiedBool) {
                statusHint = user?.pending_link ? (
                  <>
                    Reactivation link sent on <strong>{new Date(user.pending_link.created_at).toLocaleDateString()}</strong> at <strong>{new Date(user.pending_link.created_at).toLocaleTimeString()}</strong>. User can click the link in their email to reactivate.
                  </>
                ) : (
                  <>
                    User is deactivated. Use &quot;Send Reactivation Link&quot; to allow them to reactivate their account.
                  </>
                );
              } else {
                statusHint = user?.pending_link ? (
                  <>
                    Verification email sent on <strong>{new Date(user.pending_link.created_at).toLocaleDateString()}</strong> at <strong>{new Date(user.pending_link.created_at).toLocaleTimeString()}</strong>. User must click the link to verify and activate.
                  </>
                ) : (
                  <>
                    User is awaiting email verification. Use &quot;Send Verification Email&quot; to send the verification link.
                  </>
                );
              }
            }
            return (
              <div className={`user-form-status-toggle${isStatusToggleDisabled ? ' is-disabled' : ''}`}>
                <div className="user-form-status-toggle-copy">
                  <span className="user-form-status-toggle-title">
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className={`user-form-status-toggle-hint${statusHintTone}`}>
                    {statusHint}
                  </span>
                </div>
                <button
                  type="button"
                  className={`user-form-switch${formData.is_active ? ' on' : ''}`}
                  onClick={() => {
                    if (isStatusToggleDisabled) return;
                    handleChange({
                      target: {
                        name: 'is_active',
                        type: 'checkbox',
                        checked: !formData.is_active,
                      },
                    });
                  }}
                  disabled={isStatusToggleDisabled}
                  aria-pressed={!!formData.is_active}
                  aria-label={formData.is_active ? 'Active' : 'Inactive'}
                >
                  <span className="user-form-switch-knob" />
                </button>
              </div>
            );
          })()}
          </div>

          <div className="user-form-modal-footer">
            {(() => {
              const phoneValidation = formData.contact_number
                ? validateIndianPhoneNumber(formData.contact_number)
                : { isValid: true, error: null };

              const hasInvalidPhone = formData.contact_number && !phoneValidation.isValid;
              const missingEmployeeId = employeeIdMode === 'manual' && !user && !formData.employee_id?.trim();

              const isDisabled = (usernameError && !user) ||
                                 (formData.role !== 'superadmin' && selectedLocations.length === 0) ||
                                 (isDepartmentScopedRole && selectedDepartments.length === 0) ||
                                 hasInvalidPhone ||
                                 missingEmployeeId;
              return (
                <>
                  <button type="button" className="action-btn action-secondary" onClick={onCancel}>
                    <span className="action-text">Cancel</span>
                  </button>
                  {!isFormReadOnly && (
                    <button type="submit" className="action-btn action-primary" disabled={isDisabled}>
                      <span className="action-text">{user ? 'Update User' : 'Create User'}</span>
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </form>

        <ConfirmationModal
          isOpen={statusConfirmation.isOpen}
          entityName={user?.full_name}
          entityType="user"
          action={statusConfirmation.newStatus ? 'activate' : 'deactivate'}
          onConfirm={handleStatusConfirm}
          onCancel={handleStatusCancel}
          isLoading={isConfirmingStatus}
        />
      </div>
    </div>,
    document.body
  );
}

export default UserFormModal;
